import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, CheckCircle, Compass, Info, Calendar, Map, MapPin, ChevronRight, DownloadCloud } from 'lucide-react';

const ModernBanner = ({
  onScrollClick = () => {},
  regionName = 'Paisaje Agavero',
  regionSubtitle = 'Ordenamiento Ecológico Regional',
  introText,
  imageSrc = "images/Foto-Paisaje%20Agavero.jpg",
  stats = [
    { value: '5', label: 'Municipios', icon: MapPin },
    { value: '1,200', label: 'km²', icon: Map },
    { value: '2021', label: 'Publicación', icon: Calendar },
    { value: 'Vigente', label: 'Estado', icon: CheckCircle },
  ],
}) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      setMousePosition({ x, y });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section className="relative h-screen min-h-[780px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={imageSrc}
          alt={`Región ${regionName}`}
          className="w-full h-full object-cover"
          style={{
            transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.01}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-slate-950/60 to-slate-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/20 via-transparent to-slate-950/10" />
      </div>

      <div className="relative z-10 h-full flex items-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="mb-6">
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/20 to-teal-500/20 backdrop-blur-md border border-emerald-500/30">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse mr-2" />
                  <span className="text-sm font-medium text-white tracking-wider">FASE</span>
                  <div className="ml-2 pl-2 border-l border-white/20">
                    <span className="text-xs text-white/70">En Ejecución</span>
                  </div>
                </div>
              </div>

              <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
                <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">Región {regionName}</span>
              </h1>
              <p className="text-xl text-gray-200 mb-4 max-w-xl">
                {regionSubtitle}.
              </p>
              {introText && (
                <p className="text-base md:text-lg text-gray-300 mb-8 max-w-2xl leading-relaxed">
                  {introText}
                </p>
              )}
              <div className="flex flex-wrap gap-4">
                <motion.button whileHover={{ scale: 1.05 }} onClick={() => onScrollClick('introduccion')} className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-2xl transition-all">
                  Explorar Región
                </motion.button>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="hidden lg:grid grid-cols-2 gap-4">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="text-center">
                    <Icon className="mx-auto mb-2 text-cyan-200" size={24} />
                    <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-xs text-white/70 tracking-wide">{stat.label}</div>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 3, repeat: Infinity }} className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <ArrowDown className="text-cyan-400" size={32} />
      </motion.div>
    </section>
  );
};

export default ModernBanner;
export { ModernBanner };
